import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Bank, X, WarningCircle, TrendUp, TrendDown } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { formatINR, formatTenure } from "../../lib/loanMath";

const todayISO = () => new Date().toISOString().slice(0, 10);
const QUICK = [-50, -25, 25, 50];

/**
 * PortfolioRateChangeDialog — Feature 4: RBI repo change applied to the whole portfolio.
 *
 * Props:
 *   open, onClose, loans, planName, onApplied(count)
 */
export default function PortfolioRateChangeDialog({ open, onClose, loans, planName, onApplied }) {
  const [changeBps, setChangeBps] = useState(-25);
  const [effectiveDate, setEffectiveDate] = useState(todayISO());
  const [strategy, setStrategy] = useState("keep_emi"); // keep_emi | keep_tenure
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [perLoanEffective, setPerLoanEffective] = useState({});

  // Live preview
  useEffect(() => {
    if (!open) return;
    if (!loans?.length) { setPreview(null); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/loans/rate-change/portfolio-preview", {
          plan_name: planName, loans, change_bps: Number(changeBps), effective_date: effectiveDate,
        });
        if (!cancelled) {
          setPreview(data);
          // seed per-loan editable date from the batch effective date
          const seed = {};
          for (const l of data.repo_linked) seed[l.name] = effectiveDate;
          setPerLoanEffective(seed);
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Preview failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, loans, planName, changeBps, effectiveDate]);

  const applyAll = async () => {
    if (!preview?.repo_linked?.length) { toast.info("No repo-linked loans to update"); return; }
    if (!planName) { toast.error("Save the plan first before applying"); return; }
    if (!window.confirm(`Apply ${changeBps > 0 ? "+" : ""}${changeBps} bps to ${preview.repo_linked.length} loan(s)?`)) return;
    setApplying(true);
    try {
      let ok = 0;
      for (const row of preview.repo_linked) {
        const opt = row.result.options.find((o) => o.id === strategy && !o.disabled)
                  || row.result.options.find((o) => !o.disabled);
        if (!opt) continue;
        try {
          await api.post("/loans/rate-change/apply", {
            plan_name: planName,
            loan_name: row.name,
            new_rate_pct: row.new_rate_pct,
            effective_date: perLoanEffective[row.name] || effectiveDate,
            reason: "repo_change",
            option_chosen: opt.id,
            new_emi: opt.new_emi,
            new_tenure_months: opt.new_tenure_months,
            interest_remaining_before: row.result.before.interest_from_now,
            interest_remaining_after: opt.interest_from_now,
            broken_period: false,
          });
          ok += 1;
        } catch (err) {
          /* ignore individual loan errors */
          void err;
        }
      }
      toast.success(`Applied to ${ok} loan(s)`);
      onApplied?.(ok);
      onClose?.();
    } finally {
      setApplying(false);
    }
  };

  const totals = preview?.portfolio_totals;
  const interestDelta = useMemo(() => {
    if (!totals) return 0;
    return strategy === "keep_emi" ? totals.interest_delta_keep_emi : totals.interest_delta_keep_tenure;
  }, [totals, strategy]);

  if (!open) return null;
  const up = changeBps > 0, down = changeBps < 0;

  return (
    <div className="fixed inset-0 z-[10020] flex items-start sm:items-center justify-center p-2 sm:p-4" data-testid="portfolio-rate-dialog">
      <button className="absolute inset-0 bg-black/70" onClick={onClose} aria-label="Close" />
      <div className="relative card-flat w-full max-w-3xl p-5 sm:p-6 max-h-[95vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
                data-testid="portfolio-rate-close">
          <X size={16} weight="bold" />
        </button>

        <div className="eyebrow mb-1 flex items-center gap-2">
          <Bank size={14} weight="duotone" /> RBI changed the repo rate
        </div>
        <h2 className="font-serif text-2xl mb-1">Apply repo change across your floating-rate loans</h2>
        <p className="text-xs text-muted-foreground mb-4">
          Banks typically pass on a repo change at your next reset date — you can edit the effective date per loan below.
        </p>

        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <div className="sm:col-span-1">
            <div className="eyebrow text-[9px] mb-1">Change (bps)</div>
            <input type="number" step="25" value={changeBps}
                   onChange={(e) => setChangeBps(Number(e.target.value))}
                   className="input-dark w-full px-2 py-1.5 text-sm"
                   data-testid="portfolio-rate-bps" />
            <div className="flex gap-1 mt-1">
              {QUICK.map((b) => (
                <button key={b} onClick={() => setChangeBps(b)}
                        className={`px-1.5 py-0.5 text-[10px] rounded border hairline ${
                          changeBps === b ? "bg-[hsl(var(--secondary))]/15 border-[hsl(var(--secondary))]" : ""
                        }`}
                        data-testid={`portfolio-rate-quick-${b}`}>
                  {b > 0 ? "+" : ""}{b}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="eyebrow text-[9px] mb-1">Effective from</div>
            <input type="date" value={effectiveDate}
                   onChange={(e) => setEffectiveDate(e.target.value)}
                   className="input-dark w-full px-2 py-1.5 text-xs"
                   data-testid="portfolio-rate-date" />
          </div>
          <div>
            <div className="eyebrow text-[9px] mb-1">Strategy for every loan</div>
            <select value={strategy} onChange={(e) => setStrategy(e.target.value)}
                    className="input-dark w-full px-2 py-1.5 text-xs"
                    data-testid="portfolio-rate-strategy">
              <option value="keep_emi">Keep EMI, change tenure</option>
              <option value="keep_tenure">Keep tenure, change EMI</option>
            </select>
          </div>
        </div>

        {loading && !preview && <div className="text-xs text-muted-foreground mb-3">Recalculating…</div>}

        {preview && (
          <>
            {/* Portfolio totals strip */}
            {preview.repo_linked.length > 0 && (
              <div className="card-flat p-4 mb-3" data-testid="portfolio-rate-totals">
                <div className="eyebrow mb-2 text-[9px] flex items-center gap-1">
                  {up ? <TrendUp size={11} weight="duotone" className="text-destructive" />
                      : down ? <TrendDown size={11} weight="duotone" className="text-[hsl(var(--secondary))]" /> : null}
                  Portfolio impact · {strategy === "keep_emi" ? "Keep EMI" : "Keep tenure"}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                  <Stat label="Total EMI (now)" value={`₹${totals.total_emi_before.toLocaleString("en-IN")}/mo`} />
                  <Stat label="Total EMI (after)"
                        value={strategy === "keep_tenure"
                          ? `₹${totals.total_emi_after_keep_tenure.toLocaleString("en-IN")}/mo`
                          : `₹${totals.total_emi_before.toLocaleString("en-IN")}/mo`} />
                  <Stat label="Interest Δ from now"
                        value={`${interestDelta > 0 ? "+" : "−"}${formatINR(Math.abs(interestDelta), true)}`}
                        tone={interestDelta > 0 ? "destructive" : "secondary"} />
                </div>
              </div>
            )}

            {/* Repo-linked loans — editable */}
            {preview.repo_linked.length > 0 && (
              <section className="mb-4">
                <div className="eyebrow mb-2 text-[9px]">Repo-linked loans · will update</div>
                <div className="space-y-2">
                  {preview.repo_linked.map((row) => {
                    const opt = row.result.options.find((o) => o.id === strategy && !o.disabled)
                             || row.result.options.find((o) => !o.disabled);
                    return (
                      <div key={row.name} className="border hairline p-3 text-xs" data-testid={`portfolio-rate-row-${row.name}`}>
                        <div className="flex justify-between items-start gap-2 flex-wrap">
                          <div>
                            <div className="font-serif text-sm">{row.name}</div>
                            <div className="text-muted-foreground">
                              {row.current_rate_pct.toFixed(2)}% → <b>{row.new_rate_pct.toFixed(2)}%</b>
                              {" · "} {formatINR(row.outstanding, true)} outstanding
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-muted-foreground text-[10px]">EMI</div>
                            <div>₹{Math.round(row.current_emi).toLocaleString("en-IN")} → ₹{(opt?.new_emi || row.current_emi).toLocaleString("en-IN")}</div>
                            <div className="text-muted-foreground text-[10px] mt-0.5">Tenure</div>
                            <div>{formatTenure(row.remaining_months)} → {formatTenure(opt?.new_tenure_months || row.remaining_months)}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-[10px] text-muted-foreground">Effective:</span>
                          <input type="date" value={perLoanEffective[row.name] || effectiveDate}
                                 onChange={(e) => setPerLoanEffective((p) => ({ ...p, [row.name]: e.target.value }))}
                                 className="input-dark px-1.5 py-0.5 text-[11px]"
                                 data-testid={`portfolio-rate-loan-date-${row.name}`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* MCLR / Base Rate — will change at next reset */}
            {preview.mclr_wait.length > 0 && (
              <section className="mb-4">
                <div className="eyebrow mb-2 text-[9px]">MCLR / Base Rate · changes at your next reset</div>
                <div className="space-y-2">
                  {preview.mclr_wait.map((l) => (
                    <div key={l.name} className="border hairline p-2 text-xs flex justify-between" data-testid={`portfolio-rate-mclr-${l.name}`}>
                      <span>{l.name} · {l.benchmark_type}</span>
                      <span className="text-muted-foreground">
                        {l.next_reset_date
                          ? `Resets ${l.next_reset_date} — update when your bank informs you`
                          : "Will change at your next reset date — update when your bank informs you"}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Fixed — not affected */}
            {preview.fixed_na.length > 0 && (
              <section className="mb-4">
                <div className="eyebrow mb-2 text-[9px]">Fixed-rate loans · not affected</div>
                <div className="flex flex-wrap gap-1 text-[11px]">
                  {preview.fixed_na.map((l) => (
                    <span key={l.name} className="border hairline px-2 py-0.5" data-testid={`portfolio-rate-fixed-${l.name}`}>
                      {l.name}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {preview.other.length > 0 && (
              <section className="mb-4">
                <div className="eyebrow mb-2 text-[9px] flex items-center gap-1">
                  <WarningCircle size={11} weight="duotone" /> Benchmark not set
                </div>
                <div className="text-[11px] text-muted-foreground mb-1">
                  Set the benchmark type on these loans so Estima knows how to treat a repo change:
                </div>
                <div className="flex flex-wrap gap-1 text-[11px]">
                  {preview.other.map((l) => (
                    <span key={l.name} className="border hairline px-2 py-0.5" data-testid={`portfolio-rate-other-${l.name}`}>
                      {l.name}
                    </span>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        <div className="flex items-center gap-2 flex-wrap border-t hairline pt-3">
          <button onClick={applyAll}
                  disabled={applying || !preview?.repo_linked?.length || changeBps === 0}
                  className="btn-primary px-4 py-2 text-xs"
                  data-testid="portfolio-rate-apply">
            {applying ? "Applying…" : `Apply to ${preview?.repo_linked?.length || 0} loan(s)`}
          </button>
          <button onClick={onClose} className="btn-ghost px-3 py-2 text-xs" data-testid="portfolio-rate-cancel">
            Cancel
          </button>
          <div className="text-[10px] text-muted-foreground ml-auto">
            Preview only — nothing is saved until you hit Apply.
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneCls = tone === "destructive" ? "text-destructive"
                : tone === "secondary" ? "text-[hsl(var(--secondary))]" : "";
  return (
    <div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className={`num-metric text-sm ${toneCls}`}>{value}</div>
    </div>
  );
}
